import { useEffect } from 'react';

import auth from '@udacity/ureact-hoth';

import {
  ACTIVE_SUBSCRIPTION_COOKIE,
  HAS_FOCUSED_EXPERIENCE_COOKIE,
  TEAM_LEARNER_COOKIE,
  USER_TYPE_COOKIE,
} from '~/features/cookies/constants';
import { getUserType } from '~/features/user/controllers/get-user-type';
import { useUserExperience } from '~/features/user/hooks/use-user-experience';

import { deleteAuthedUserCookies } from '../utils/delete-authed-user-cookies';
import { getActiveSubscriptionCookie } from '../utils/get-active-subscription-cookie';
import { getHasFocusedCookie } from '../utils/get-has-focused-cookie';
import { getTeamLearnerCookie } from '../utils/get-team-learner-cookie';
import { getUserTypeCookie } from '../utils/get-user-type-cookie';
import { setClientCookie } from '../utils/set-client-cookie';

/**
 * Reconciles auth-related cookies with the current user experience data.
 * Piggybacks on the shared React Query cache populated by useUserExperience
 * rather than making a separate fetch to experience-api.
 *
 * Must be rendered inside QueryClientProvider.
 */
export function useCheckUserCookies() {
  const { userExperience } = useUserExperience();

  useEffect(() => {
    const jwt = auth.getJWT();

    if (!jwt) {
      deleteAuthedUserCookies();
      return;
    }

    if (!userExperience) return;

    const { hasActiveSubscription, teams, hasFocused } = userExperience;
    const userActiveSubscriptionCookie = getActiveSubscriptionCookie();
    const teamLearnerCookie = getTeamLearnerCookie();
    const hasFocusedCookie = getHasFocusedCookie();

    if (!hasActiveSubscription && userActiveSubscriptionCookie) {
      setClientCookie(ACTIVE_SUBSCRIPTION_COOKIE, 'false');
    }

    if (hasActiveSubscription && !userActiveSubscriptionCookie) {
      setClientCookie(ACTIVE_SUBSCRIPTION_COOKIE, 'true');
    }

    if (teams && !teamLearnerCookie) {
      setClientCookie(TEAM_LEARNER_COOKIE, 'true');
    }

    if (!teams && teamLearnerCookie) {
      setClientCookie(TEAM_LEARNER_COOKIE, 'false');
    }

    if (hasFocused && !hasFocusedCookie) {
      setClientCookie(HAS_FOCUSED_EXPERIENCE_COOKIE, 'true');
    }

    if (!hasFocused && hasFocusedCookie) {
      setClientCookie(HAS_FOCUSED_EXPERIENCE_COOKIE, 'false');
    }
  }, [userExperience]);

  useEffect(() => {
    async function reconcileUserType() {
      const jwt = auth.getJWT();
      if (!jwt) return;

      const userTypeCookie = getUserTypeCookie();
      const userType = await getUserType();

      if (userType && userTypeCookie !== userType) {
        setClientCookie(USER_TYPE_COOKIE, userType);
      }
    }

    reconcileUserType();
  }, []);
}
